import { Controller } from "@hotwired/stimulus"

// Sign-in form telemetry (#15570). On submit, fills the hidden
// login_telemetry[...] fields with what the browser observed: time from the
// first keystroke or paste to submit, whether anything was typed or pasted,
// how many times this browser submitted today, and screen, time zone,
// language and processor count. It never reads or sends what was typed.
//
// Everything fails open: a blocked localStorage or a missing browser API
// leaves that field blank, and nothing here can stop the form submitting.
const STORAGE_KEY = "gl_login_attempts"

export default class extends Controller {
  static targets = [
    "formCompletionMs", "typed", "pasted", "dailyAttemptCount", "screenWidth",
    "screenHeight", "timeZone", "language", "logicalProcessors"
  ]
  static values = { browserId: String }

  connect() {
    this.startedAt = null
    this.typedSeen = false
    this.pastedSeen = false
  }

  trackInput(event) {
    this.markStarted()
    if (event.inputType && event.inputType !== "insertFromPaste") this.typedSeen = true
  }

  trackPaste() {
    this.markStarted()
    this.pastedSeen = true
  }

  capture() {
    this.safely(() => {
      const elapsed = this.startedAt === null ? "" : Math.round(performance.now() - this.startedAt)
      this.fill("formCompletionMs", elapsed)
    })
    this.fill("typed", this.typedSeen)
    this.fill("pasted", this.pastedSeen)
    this.safely(() => this.fill("dailyAttemptCount", this.bumpDailyAttempts()))
    this.safely(() => {
      this.fill("screenWidth", window.screen.width)
      this.fill("screenHeight", window.screen.height)
    })
    this.safely(() => this.fill("timeZone", Intl.DateTimeFormat().resolvedOptions().timeZone))
    this.safely(() => this.fill("language", navigator.language))
    this.safely(() => this.fill("logicalProcessors", navigator.hardwareConcurrency))
  }

  // private

  markStarted() {
    if (this.startedAt === null) this.startedAt = performance.now()
  }

  // Attempts from THIS browser on the current UTC day; resets when either
  // changes. Without a browser ID there is nothing to key on, so it is blank.
  bumpDailyAttempts() {
    if (!this.browserIdValue) return ""

    const day = new Date().toISOString().slice(0, 10)
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null")
    const count = saved && saved.browserId === this.browserIdValue && saved.day === day ? saved.count + 1 : 1
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ browserId: this.browserIdValue, day, count }))
    return count
  }

  fill(name, value) {
    const target = this[`${name}Target`]
    if (target) target.value = value === undefined || value === null ? "" : String(value)
  }

  safely(fn) {
    try { fn() } catch (_error) { /* fail open: leave the field blank */ }
  }
}
